import React from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { borderRadius, getControlBorderColor, getSurfaceBorderColor, layoutGap } from '../../../theme/theme';

export interface WorkspaceIntroStep {
  label: string;
  goal: string;
  checks?: readonly string[];
}

export interface WorkspaceIntroSection {
  title: string;
  bullets: readonly string[];
}

export interface WorkspaceIntroCardProps {
  eyebrow: string;
  title: string;
  summary: string;
  hint: string;
  status?: string | null;
  chips?: readonly string[];
  sections?: readonly WorkspaceIntroSection[];
  steps?: readonly WorkspaceIntroStep[];
  actionLabel?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
}

const sectionTitleSx = {
  fontSize: '0.68rem',
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
};

const normalizeBullets = (bullets: readonly string[] | undefined) =>
  Array.from(
    new Set(
      (bullets ?? [])
        .map((bullet) => bullet.trim())
        .filter(Boolean)
    )
  );

const WorkspaceIntroCard: React.FC<WorkspaceIntroCardProps> = ({
  eyebrow,
  title,
  summary,
  hint,
  status,
  chips = [],
  sections = [],
  steps = [],
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
}) => {
  const theme = useTheme();
  const surfaceBorderColor = getSurfaceBorderColor(theme);
  const renderedSections = sections
    .map((section) => ({
      title: section.title.trim(),
      bullets: normalizeBullets(section.bullets),
    }))
    .filter((section, index, allSections) => {
      if (!section.title || section.bullets.length === 0) {
        return false;
      }

      return (
        allSections.findIndex(
          (candidate) => candidate.title.toLocaleLowerCase() === section.title.toLocaleLowerCase()
        ) === index
      );
    })
    .slice(0, 4);
  const renderedSteps = steps
    .map((step) => ({
      label: step.label.trim(),
      goal: step.goal.trim(),
      checks: normalizeBullets(step.checks),
    }))
    .filter((step) => step.label && step.goal)
    .slice(0, 5);

  return (
    <Paper
      elevation={0}
      sx={{
        mx: 0.4,
        mb: 0.35,
        px: 1.25,
        py: 1,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: `${borderRadius.lg}px`,
        border: `1px solid ${alpha(theme.palette.divider, 0.16)}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.68),
        backdropFilter: 'blur(10px)',
        boxShadow: `0 8px 18px ${alpha(theme.palette.common.black, 0.12)}`,
      }}
    >
      <Stack spacing={layoutGap.row}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={layoutGap.section}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ ...sectionTitleSx, color: alpha(theme.palette.primary.light, 0.88) }}>
              {eyebrow}
            </Typography>
            <Typography variant="subtitle2" sx={{ mt: 0.35, fontWeight: 700 }}>
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.35, color: alpha(theme.palette.text.secondary, 0.88), maxWidth: 760 }}
            >
              {summary}
            </Typography>
            {chips.length > 0 && (
              <Stack direction="row" spacing={layoutGap.blend} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                {chips.map((chip) => (
                  <Chip
                    key={chip}
                    size="small"
                    label={chip}
                    sx={{
                      height: 20,
                      borderRadius: `${borderRadius.full}px`,
                      fontSize: '0.66rem',
                      fontWeight: 700,
                      backgroundColor: alpha(theme.palette.background.default, 0.12),
                      border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>

          <Stack spacing={layoutGap.cozy} useFlexGap sx={{ minWidth: { md: 260 } }}>
            <Typography sx={{ ...sectionTitleSx, color: alpha(theme.palette.text.secondary, 0.78) }}>
              Best next move
            </Typography>
            <Typography variant="body2" sx={{ color: alpha(theme.palette.text.primary, 0.96) }}>
              {hint}
            </Typography>
            {status && (
              <Chip
                size="small"
                label={status}
                sx={{
                  alignSelf: 'flex-start',
                  height: 20,
                  borderRadius: `${borderRadius.full}px`,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  border: `1px solid ${surfaceBorderColor}`,
                }}
              />
            )}
            {(secondaryActionLabel && onSecondaryAction) || (actionLabel && onAction) ? (
              <Stack direction="row" spacing={layoutGap.blend} flexWrap="wrap" useFlexGap sx={{ mt: 3 }}>
                {secondaryActionLabel && onSecondaryAction && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={onSecondaryAction}
                    sx={{
                      alignSelf: 'flex-start',
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {secondaryActionLabel}
                  </Button>
                )}
                {actionLabel && onAction && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onAction}
                    sx={{
                      alignSelf: 'flex-start',
                      borderRadius: `${borderRadius.md}px`,
                      borderColor: getControlBorderColor(theme),
                      backgroundColor: alpha(theme.palette.background.default, 0.1),
                      '&:hover': {
                        borderColor: getControlBorderColor(theme),
                      },
                    }}
                  >
                    {actionLabel}
                  </Button>
                )}
              </Stack>
            ) : null}
          </Stack>
        </Stack>

        {renderedSections.length > 0 && (
          <Box>
            <Typography sx={{ ...sectionTitleSx, color: alpha(theme.palette.text.secondary, 0.82), mb: 0.65 }}>
              What To Understand
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: renderedSections.map((section) => `${section.bullets.join(' ').length}fr`).join(' ') },
                gap: layoutGap.row,
              }}
            >
              {renderedSections.map((section) => (
                <Box
                  key={section.title}
                  sx={{
                    minWidth: 0,
                    px: 1,
                    py: 0.7,
                    borderRadius: `${borderRadius.md}px`,
                    border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
                    backgroundColor: alpha(theme.palette.background.default, 0.1),
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: alpha(theme.palette.text.primary, 0.96),
                      lineHeight: 1.35,
                    }}
                  >
                    {section.title}
                  </Typography>
                  <Box component="ul" sx={{ mt: 0.45, mb: 0, pl: 2.1 }}>
                    {section.bullets.map((bullet) => (
                      <Typography
                        key={bullet}
                        component="li"
                        variant="body2"
                        sx={{
                          color: alpha(theme.palette.text.secondary, 0.88),
                          lineHeight: 1.45,
                          '& + li': {
                            mt: 0.3,
                          },
                        }}
                      >
                        {bullet}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {renderedSteps.length > 0 && (
          <Box>
            <Typography sx={{ ...sectionTitleSx, color: alpha(theme.palette.text.secondary, 0.82), mb: 0.65 }}>
              Suggested Walkthrough
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: `repeat(${Math.min(renderedSteps.length, 5)}, minmax(0, 1fr))`,
                },
                gap: layoutGap.row,
              }}
            >
              {renderedSteps.map((step) => (
                <Box
                  key={step.label}
                  sx={{
                    minWidth: 0,
                    px: 1,
                    py: 0.6,
                    borderRadius: `${borderRadius.md}px`,
                    border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
                    backgroundColor: alpha(theme.palette.background.default, 0.08),
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: alpha(theme.palette.text.secondary, 0.78),
                      letterSpacing: '0.03em',
                      fontWeight: 700,
                    }}
                  >
                    {step.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.35,
                      color: alpha(theme.palette.text.primary, 0.94),
                      lineHeight: 1.38,
                      flex: 1,
                    }}
                  >
                    {step.goal}
                  </Typography>
                  {step.checks.length > 0 && (
                    <Box sx={{ mt: 'auto', pt: 0.6 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          color: alpha(theme.palette.text.secondary, 0.76),
                          fontWeight: 700,
                          letterSpacing: '0.03em',
                          textTransform: 'uppercase',
                        }}
                      >
                        Checks
                      </Typography>
                      <Box component="ul" sx={{ mt: 0.4, mb: 0, pl: 2 }}>
                        {step.checks.map((check) => (
                          <Typography
                            key={check}
                            component="li"
                            variant="body2"
                            sx={{
                              color: alpha(theme.palette.text.secondary, 0.84),
                              lineHeight: 1.36,
                              '& + li': {
                                mt: 0.25,
                              },
                            }}
                          >
                            {check}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export default WorkspaceIntroCard;
